import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Container, Button, Badge, Nav, Offcanvas, Spinner
} from "react-bootstrap";
import {
    FaSignOutAlt, FaBox, FaUsers, FaBars, FaUser, FaChevronLeft,
    FaChevronRight, FaNetworkWired, FaHistory, FaLock, FaChartBar, FaWpforms, FaThList, FaDollarSign
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import ProductsList from "../Components/Dashboard/Products";
import Customers from "../Components/Dashboard/Customers";
import Users from "../Components/Dashboard/Users";
import Reports from "../Components/Dashboard/Reports";
import Collection from "../Components/Dashboard/Collection";
import Access from "../Components/Dashboard/Access";
import LoginActivity from "../Components/Dashboard/LogActivity";
import PassReset from "../Components/Dashboard/PassReset";
import Application from "../Components/Dashboard/Application";
import CollectionFee from "../Components/Dashboard/CollectionFee";
import { getRoleBadge } from "../Components/Shared/GetRoleBadge";
import HolidayDisplay from "../Components/Shared/HolidayDisplay";
import GraphTrends from "../Components/Shared/GraphTrends";
import Snowfall from 'react-snowfall';
import { motion } from 'framer-motion'

const DashboardPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [userName, setUserName] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [userAccess, setUserAccess] = useState(null);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const hasFetched = useRef(false);
    const isLoggingOut = useRef(false);
    const [singaporeTime, setSingaporeTime] = useState(new Date());
    const eyeballs = useRef([]);

    const lookAtMouse = useCallback((x, y) => {
        eyeballs.current.forEach(eyeball => {
            const rect = eyeball.getBoundingClientRect();
            const dx = x - (rect.left + rect.width / 2);
            const dy = y - (rect.top + rect.height / 2);
            const dist = Math.min(6, Math.hypot(dx, dy) / 15);
            const angle = Math.atan2(dy, dx);
            eyeball.style.transform = `translate(${Math.cos(angle) * dist}px, ${Math.sin(angle) * dist}px)`;
        });
    }, []);

    useEffect(() => {
        const timer = setInterval(() => setSingaporeTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const handleMouse = (e) => lookAtMouse(e.clientX, e.clientY);
        document.addEventListener('mousemove', handleMouse);
        return () => document.removeEventListener('mousemove', handleMouse);
    }, [lookAtMouse]);

    const formattedTime = singaporeTime.toLocaleTimeString("en-US", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true, timeZone: "Asia/Singapore"
    });
    const formattedDate = singaporeTime.toLocaleDateString("en-US", {
        weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "Asia/Singapore"
    });

    useEffect(() => {
        const fetchUser = async () => {
            if (hasFetched.current || isLoggingOut.current) return;
            hasFetched.current = true;

            try {
                const res = await axios.get("https://www.azzenterprises.com/api/admin/getuser", { withCredentials: true });
                if (!res.data || !res.data.user_name) {
                    toast.error("Please login to access the dashboard");
                    setIsAuthenticated(false);
                    return;
                }
                setUserName(res.data.user_name);
                setUserRole(res.data.user_role);
                setUserAccess(res.data.can_access);
                setIsAuthenticated(true);
                localStorage.setItem("user_name", res.data.user_name);
                localStorage.setItem("user_role", res.data.user_role);
                localStorage.setItem("can_access", res.data.can_access);
            } catch (error) {
                console.error("Fetch user error:", error);
                toast.error("Please login to access the dashboard");
                setIsAuthenticated(false);
            }
        };

        const cachedUser = localStorage.getItem("user_name");
        const cachedRole = localStorage.getItem("user_role");
        const cachedAccess = localStorage.getItem("can_access");

        if (cachedUser && cachedRole) {
            setUserName(cachedUser);
            setUserRole(cachedRole);
            setUserAccess(cachedAccess);
            setIsAuthenticated(true);
        } else {
            fetchUser();
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated === false && !isLoggingOut.current) {
            localStorage.removeItem("user_name");
            localStorage.removeItem("user_role");
            localStorage.removeItem("can_access");
            hasFetched.current = false;

            navigate("/login", { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const ROLE_CONFIG = {
        superadmin: [
            { path: "/products-list", label: "Products", icon: <FaBox /> },
            { path: "/user", label: "Users", icon: <FaUser /> },
            { path: "/access", label: "Access", icon: <FaNetworkWired /> },
            { path: "/activity", label: "Activity", icon: <FaHistory /> },
            { path: "/reset-password", label: "Password", icon: <FaLock /> },
            { path: "/collection", label: "Collection", icon: <FaThList /> },
            { path: "/reports", label: "Reports", icon: <FaChartBar /> },
            { path: "/application", label: "Application", icon: <FaWpforms /> },
            { path: "/processing-fee", label: "Proc Fee", icon: <FaDollarSign /> }
        ],
        supervisor: [
            { path: "/application", label: "Application", icon: <FaWpforms /> }
        ],
        viewer: [
            { path: "/products-list", label: "Products", icon: <FaBox /> },
            { path: "/application", label: "Application", icon: <FaWpforms /> }
        ],
        editor: [
            { path: "/products-list", label: "Products", icon: <FaBox /> },
            { path: "/application", label: "Application", icon: <FaWpforms /> }
        ]
    };

    const navItems = [
        { path: "/customers", label: "Customers", icon: <FaUsers /> },
        ...(ROLE_CONFIG[userRole] || []),
    ];

    const handleLogout = async () => {
        if (isLoggingOut.current) return;

        isLoggingOut.current = true;
        try {
            await axios.post("https://www.azzenterprises.com/api/admin/logout", {}, {
                withCredentials: true
            });
        } catch (error) {
            console.error("Logout API error:", error);
        } finally {
            localStorage.removeItem("user_name");
            localStorage.removeItem("user_role");
            localStorage.removeItem("can_access");

            setUserName(null);
            setUserRole(null);
            setUserAccess(null);
            setIsAuthenticated(false);
            hasFetched.current = false;

            toast.success("Logged out successfully");

            setTimeout(() => {
                navigate("/login", { replace: true });
            }, 1000);
        }
    };

    const pageTitles = {
        "/customers": "Customers",
        "/products-list": "Products",
        "/user": "Role-Based Access (RBAC)",
        "/access": "Customer Privileges",
        "/activity": "Staff Activity Log",
        "/reset-password": "Change Password",
        "/collection": "Collection Management - Overdue",
        "/reports": "EMI Payment & Collection Reports",
        "/application": "Application",
        "/processing-fee": "Processing Fee Management",
    };

    const getPageSubtitle = (pathname) => {
        const subtitles = {
            "/customers": "Customer Management Dashboard",
            "/products-list": "EMI Products Catalog",
            "/user": "Role & Permission Settings",
            "/access": "Customer Access Control",
            "/activity": "Staff Activity Monitor",
            "/reset-password": "Security Settings",
            "/collection": "Overdue Collections Dashboard",
            "/reports": "EMI Analytics & Reports",
            "/application": "Application Dashboard",
            "/processing-fee": "Fee Management System",
            "/": "Main Dashboard"
        };

        return subtitles[pathname] || "EMI Analytics Dashboard";
    };


    const renderContent = () => {
        if (isAuthenticated === null) {
            return (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            );
        }

        if (!isAuthenticated) {
            return null;
        }

        switch (location.pathname) {
            case "/customers": return <Customers />;
            case "/products-list": return <ProductsList />;
            case "/user": return <Users />;
            case "/access": return <Access />;
            case "/activity": return <LoginActivity />;
            case "/reset-password": return <PassReset />;
            case "/collection": return <Collection />;
            case "/reports": return <Reports />;
            case "/application": return <Application />;
            case "/processing-fee": return <CollectionFee />;

            default:
                return (
                    <div className="p-4 text-center">
                        <h4 className="fw-bold mb-3">Welcome to Your Dashboard</h4>
                        <p className="text-muted">Here's what's happening with your store today.</p>
                        <Button variant="outline-primary" onClick={() => navigate("/products-list")}>
                            Explore Products
                        </Button>
                    </div>
                );
        }
    };

    if (isAuthenticated === null) {
        return (
            <>
                <ToastContainer position="top-right" autoClose={1000} />
                <div className="d-flex justify-content-center align-items-center min-vh-100">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </>
        );
    }

    if (!isAuthenticated) {
        return (
            <>
                <ToastContainer position="top-right" autoClose={1000} />
                <div className="d-flex justify-content-center align-items-center min-vh-100">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Redirecting to login...</span>
                    </Spinner>
                </div>
            </>
        );
    }

    const getMessage = (index) => {
        const messages = [
            <small key="1" className="badge bg-danger-subtle text-danger-emphasis px-3 py-1 me-5">
                ⚠️ Any product delivered without confirmed eGIRO creation will be the joint responsibility of the Sales and Accounts teams.
            </small>,

            <small key="2" className="badge bg-warning-subtle text-warning-emphasis px-3 py-1 me-5">
                📢 Sales and Accounts must verify eGIRO status and customer consent before approving product delivery.
            </small>,

            <small key="3" className="badge bg-info-subtle text-info-emphasis px-3 py-1 me-5">
                ⚠️ Failed or pending eGIRO attempts must be retried, recorded, and escalated by Sales or Accounts within 24 hours.
            </small>,

            <small key="4" className="badge bg-success-subtle text-success-emphasis px-3 py-1 me-5">
                ✅ Ensure accurate bank details and explicit customer consent to prevent eGIRO processing delays.
            </small>,

            <small key="5" className="badge bg-primary-subtle text-primary-emphasis px-3 py-1 me-5">
                ⏰ If eGIRO status is unavailable, Sales and Accounts must recheck bank systems and retry after 30 minutes.
            </small>,

            <small key="6" className="badge bg-danger-subtle text-danger-emphasis px-3 py-1 me-5">
                🚫 Product delivery must not proceed unless eGIRO status is marked as “Successful” or formally approved.
            </small>,

            <small key="7" className="badge bg-warning-subtle text-warning-emphasis px-3 py-1 me-5">
                📄 All eGIRO-related exceptions must be documented with reason and timestamp by Sales or Accounts.
            </small>,

            <small key="8" className="badge bg-info-subtle text-info-emphasis px-3 py-1 me-5">
                🔄 Reattempt eGIRO setup immediately if bank validation fails or customer details are incomplete.
            </small>,

            <small key="9" className="badge bg-success-subtle text-success-emphasis px-3 py-1 me-5">
                🤝 Sales and Accounts must coordinate closely to ensure smooth and compliant eGIRO onboarding.
            </small>,

            <small key="10" className="badge bg-primary-subtle text-primary-emphasis px-3 py-1 me-5">
                🛡️ Proper eGIRO compliance protects both the customer experience and internal accountability.
            </small>
        ];

        return messages[index % messages.length];
    };

    const messages = [
        getMessage(0),
        getMessage(1),
        getMessage(2),
        getMessage(3),
        getMessage(4),
    ];

    return (
        <Container fluid className="bg-light min-vh-100 p-0 d-flex">
            <div className="d-md-none shadow-sm bg-white p-2 sticky-top w-100" style={{ zIndex: 1030 }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <Button variant="light" className="me-2" onClick={() => setShowMobileMenu(true)}>
                            <FaBars />
                        </Button>
                        <span className="fw-semibold">Welcome,</span>
                        <Badge bg="light" text="dark" className="ms-2">{userName}</Badge>
                    </div>
                    <Button onClick={handleLogout} variant="outline-danger" size="sm" className="d-flex align-items-center gap-2">
                        <FaSignOutAlt />
                        <span className="d-none d-md-inline">Logout</span>
                    </Button>
                </div>
            </div>

            <div
                className="d-none d-md-flex flex-column bg-white shadow-sm sidebar-sticky transition-all"
                style={{
                    width: isSidebarCollapsed ? "60px" : "20%",
                    minWidth: isSidebarCollapsed ? "60px" : "170px",
                    maxWidth: isSidebarCollapsed ? "60px" : "170px",
                    height: "100vh",
                    position: "sticky",
                    top: 0,
                    overflowY: "auto",
                    zIndex: 1020,
                    flexShrink: 0
                }}
            >
                <div className="p-2 border-bottom d-flex justify-content-end align-items-center">
                    <Button
                        variant="light"
                        size="sm"
                        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                        className="rounded-circle p-1 shadow-none hover-lift"
                    >
                        {isSidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
                    </Button>
                </div>

                {!isSidebarCollapsed && (
                    <div className="p-3 border-bottom bg-light">
                        <div className="text-muted fs-2xs mb-1">Welcome</div>
                        <Badge bg="success" className="fw-semibold text-xs">{userName}</Badge>
                    </div>
                )}

                <Nav className="flex-column flex-grow-1 px-2 py-3">
                    {navItems.map((item) => (
                        <Nav.Link
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={`nav-item py-1.5 px-3 rounded-2 mb-1 d-flex align-items-center transition-all fs-2xs ${location.pathname === item.path
                                ? "bg-primary text-white shadow-sm active-nav"
                                : "text-muted hover-nav"
                                }`}
                            style={{ cursor: "pointer" }}
                        >
                            <span className="me-2 fs-1xs">{item.icon}</span>
                            {!isSidebarCollapsed && <span className="fw-medium">{item.label}</span>}
                        </Nav.Link>

                    ))}
                </Nav>

                {!isSidebarCollapsed && (
                    <div className="p-2 border-top mt-auto">
                        <Button
                            onClick={handleLogout}
                            variant="outline-danger"
                            size="sm"
                            className="w-100 rounded-2 py-1 px-2 fs-2xs d-flex align-items-center justify-content-center gap-1 hover-danger transition-all"
                        >
                            <FaSignOutAlt className="fs-1xs me-1" />
                            Logout
                        </Button>
                    </div>
                )}
            </div>

            <div className="flex-grow-1 p-3 position-relative overflow-hidden" style={{
                width: isSidebarCollapsed ? "calc(100% - 60px)" : "80%",
                minHeight: "100vh",
                background: 'linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 100%)'
            }}>
                <div className="glass-header-soft position-relative overflow-hidden snow-container">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 p-md-4 gap-3">
                        <div>
                            <h5 className="fw-bold mb-1 text-dark drop-shadow-soft">
                                {pageTitles[location.pathname] || "Dashboard"}
                            </h5>
                            <div className="bg-white bg-opacity-30 backdrop-blur-sm rounded-2 px-2 py-1 text-primary fw-semibold shadow-sm"
                                style={{ fontSize: '0.8rem' }}>
                                {getPageSubtitle(location.pathname)}
                            </div>
                        </div>

                        <div className="text-end position-relative" >
                            <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">

                                <div className="flex-grow-1 text-center d-none d-md-block">
                                    <GraphTrends />
                                </div>

                                <div className="flex-grow-1 text-center d-none d-md-block">
                                    <HolidayDisplay />
                                </div>

                                <div className="glass-info-soft rounded-2 p-2 border border-light shadow-sm position-relative z-10 d-flex flex-column align-items-center">
                                    <div className="text-muted-50 xsmall mb-1 fw-medium" style={{ fontSize: '0.65rem' }}>{formattedDate}</div>
                                    <div className="fw-semibold text-dark" style={{ fontSize: '0.85rem' }}>{formattedTime}</div>
                                    <div className="mt-1 d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                                        {getRoleBadge(userRole)}
                                        <motion.div
                                            className="rounded-circle position-relative"
                                            style={{
                                                width: '6px',
                                                height: '6px',
                                                backgroundColor: '#10b981',
                                                boxShadow: '0 0 8px rgba(16, 185, 129, 0.6)'
                                            }}
                                            animate={{
                                                scale: [1, 1.3, 1],
                                                boxShadow: [
                                                    '0 0 8px rgba(16, 185, 129, 0.6)',
                                                    '0 0 12px rgba(16, 185, 129, 0.8)',
                                                    '0 0 8px rgba(16, 185, 129, 0.6)'
                                                ]
                                            }}
                                            transition={{
                                                duration: 1.5,
                                                repeat: Infinity,
                                                ease: "easeInOut"
                                            }}
                                        >
                                            <motion.div
                                                className="position-absolute rounded-circle"
                                                style={{
                                                    width: '10px',
                                                    height: '10px',
                                                    border: '2px solid rgba(16, 185, 129, 0.4)',
                                                    top: '-1px',
                                                    left: '-1px'
                                                }}
                                                animate={{
                                                    scale: [1, 1.4, 1],
                                                    opacity: [0.4, 0.1, 0.4]
                                                }}
                                                transition={{
                                                    duration: 2,
                                                    repeat: Infinity,
                                                    ease: "easeOut",
                                                    delay: 0.5
                                                }}
                                            />
                                        </motion.div>
                                        <span className="text-success fw-semibold" style={{ fontSize: '0.5rem' }}>Online</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="snow-layers relative">
                        <Snowfall
                            color="#e6e9ff"
                            snowflakeCount={30}
                            speed={[0.05, 0.2]}
                            radius={[0.5, 2]}
                            wind={[-0.1, 0.1]}
                            rotationSpeed={[-0.5, 0.5]}
                        />
                        <Snowfall
                            color="#d1d5ff"
                            snowflakeCount={20}
                            speed={[0.1, 0.3]}
                            radius={[1, 3]}
                            wind={[-0.2, 0.2]}
                            rotationSpeed={[-1, 1]}
                        />
                        <Snowfall
                            color="#a5b4fc"
                            snowflakeCount={8}
                            speed={[0.15, 0.4]}
                            radius={[1.5, 4]}
                            wind={[-0.3, 0.3]}
                            rotationSpeed={[-1.5, 1.5]}
                        />
                    </div>
                </div>

                <div
                    style={{
                        position: "absolute",
                        top: "15px",
                        left: 0,
                        right: 0,
                        height: "30px",
                        width: "100vw",
                        fontSize: '13px'
                    }}
                >
                    <motion.div
                        className="d-flex align-items-center"
                        style={{
                            whiteSpace: "nowrap",
                        }}
                        animate={{ x: ["0%", "-50%"] }}
                        transition={{
                            duration: 60,
                            ease: "linear",
                            repeat: Infinity,
                        }}
                    >
                        {messages.map((msg, i) => (
                            <div key={`a-${i}`} style={{ marginRight: "50px" }}>
                                {msg}
                            </div>
                        ))}

                        {messages.map((msg, i) => (
                            <div key={`b-${i}`} style={{ marginRight: "50px" }}>
                                {msg}
                            </div>
                        ))}
                    </motion.div>
                </div>

                <div className="content-card-soft bg-white/90 rounded-3 shadow-md border border-light overflow-hidden">
                    <div className="p-2 bg-white">{renderContent()}</div>
                </div>
            </div>

            <Offcanvas show={showMobileMenu} onHide={() => setShowMobileMenu(false)} placement="start" className="w-75">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title className="fw-bold">Dashboard</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="p-0">
                    <Nav className="flex-column">
                        {navItems.map((item) => (
                            <Nav.Link
                                key={item.path}
                                onClick={() => {
                                    navigate(item.path);
                                    setShowMobileMenu(false);
                                }}
                                className={`px-4 py-3 d-flex align-items-center border-bottom ${location.pathname === item.path ? "bg-primary text-white" : "text-dark"
                                    }`}
                            >
                                <span className="me-3">{item.icon}</span>
                                {item.label}
                            </Nav.Link>
                        ))}
                    </Nav>
                </Offcanvas.Body>
            </Offcanvas>
        </Container>
    );
};

export default DashboardPage;